import React, { FC, useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Link } from "react-router-dom"
import { Modal, ModalBody, ModalFooter, ModalHeader, Button, CustomInput } from "reactstrap"
import { ApplicationState } from "../../core/types"
import { User } from "../../data/model/user-model"
import { isCloudApi, getSystemRegistration, ResourceNotFoundError } from "../../data/api"
import { isAuthorized } from "../ProtectedRoute"
import { useUserPreferences } from "../../data/stores/user-preferences/use-user-preferences"
import { saveUserPrefences } from "../../data/stores/user-preferences/user-preferences-actions"
import {
    UserPreferences,
    RegistrationPreferences,
    defaultUserPreference,
} from "../../data/model/user-preferences-model"
import { RegistrationForm } from "../../silos/configuration/components/RegistrationForm"

interface Props {
    // The EULA has been settled for this admin; the registration modal may now
    // sequence itself in (it never appears ahead of the User Agreement).
    eulaResolved: boolean
}

// Outer gate: only on-prem admins are candidates. Kept separate so the inner
// component's hooks (notably useUserPreferences, which issues a GET) never run
// for non-admins or cloud users — i.e. for most page loads.
export const RegistrationModal: FC<Props> = ({ eulaResolved }) => {
    const user = useSelector<ApplicationState, User>(state => state.auth.user)
    if (!user || !isAuthorized(user, "admin") || isCloudApi()) { return null }
    return <RegistrationModalForAdmin eulaResolved={eulaResolved} />
}

const RegistrationModalForAdmin: FC<Props> = ({ eulaResolved }) => {
    const prefs = useUserPreferences()
    const dispatch = useDispatch()

    const [isOpen, setIsOpen] = useState(false)
    const [registered, setRegistered] = useState(false)
    const [dontShowAgain, setDontShowAgain] = useState(false)
    // Open the modal exactly once per mount, even as preferences re-render us.
    const hasOpenedRef = useRef(false)

    useEffect(() => {
        let cancelled = false
        // Wait for the EULA and loaded prefs; skip if this admin has opted out.
        // Then confirm with the server that the system is not already registered
        // before prompting. Runs its decision only once.
        const ready = !hasOpenedRef.current && eulaResolved
            && !!prefs && !prefs.registrationPreferences?.modalDismissed
        if (ready) {
            hasOpenedRef.current = true
            getSystemRegistration()
                .then(() => { /* already registered — never prompt */ })
                .catch(err => {
                    if (!cancelled && err instanceof ResourceNotFoundError) {
                        setIsOpen(true)
                    }
                })
        }
        return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eulaResolved, prefs])

    const persistRegistrationPrefs = (patch: Partial<RegistrationPreferences>) => {
        const base: UserPreferences = prefs || defaultUserPreference
        const nextPrefs: UserPreferences = {
            ...base,
            registrationPreferences: {
                ...defaultUserPreference.registrationPreferences,
                ...base.registrationPreferences,
                ...patch,
            },
        }
        dispatch(saveUserPrefences(nextPrefs))
    }

    // Deferring closes the modal for this session. If the admin ticked "Don't
    // show again", that choice is committed here so it survives future logins.
    const closeModal = () => {
        if (dontShowAgain) {
            persistRegistrationPrefs({ modalDismissed: true })
        }
        setIsOpen(false)
    }

    return (
        <Modal size="lg" isOpen={isOpen} toggle={closeModal} aria-label="Register SMRT Link system">
            <ModalHeader toggle={closeModal}>Register your SMRT Link system</ModalHeader>
            <ModalBody>
                <RegistrationForm onRegistered={() => setRegistered(true)} />
            </ModalBody>
            <ModalFooter>
                {registered ? (
                    <Button color="primary" onClick={closeModal}>Close</Button>
                ) : (
                    <>
                        <div className="mr-auto">
                            <CustomInput
                                type="checkbox"
                                id="registration-dont-show-again"
                                label="Don't show again"
                                checked={dontShowAgain}
                                onChange={e => setDontShowAgain(e.target.checked)}
                            />
                            {dontShowAgain && (
                                <small className="text-muted d-block mt-1">
                                    You may still register SMRT Link by visiting the{" "}
                                    <Link to="/settings/register" onClick={closeModal}>Registration</Link>{" "}
                                    Settings page.
                                </small>
                            )}
                        </div>
                        <Button color="secondary" onClick={closeModal}>
                            {dontShowAgain ? "Close" : "Remind me later"}
                        </Button>
                    </>
                )}
            </ModalFooter>
        </Modal>
    )
}
